/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useEffect, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
// utils
import {
    fetchUniqueTriviaUsersReport,
    fetchTotalAnswersReport,
    fetchTopCardsReport,
    fetchTopSkippedReport,
    fetchTopSkippedCardsExportAll,
    fetchTopPollsExportAll,
    fetchTriviaEngagementRateReport,
    fetchAvgInViewReport
} from '../../../../state/thunks/triviapolls';
import { fetchProductDataLastUpdated } from '../../../../state/thunks/product';
import { getDevices } from '../../../../utils/filterhelpers';

// material
import { Page, Content, InContentMessage } from '@insticator/insticator-ui';
import Header from '../../../../material/structure/header/header';
import PageHeader from '../../../../material/structure/pageheader/pageheader';
import PageContent from '../../../../material/structure/pagecontent/pagecontent';
import GlobalFilter from '../../../../material/globalfilter/globalfilter';

// styles
import injectSheet from 'react-jss';
import styles from './triviapollsanalytics_styles';
// Content
import TopPollandQuizReport from "./toppollandquizreport/toppollandquizreport";
import TotalAnswersReport from './totalanswersreport/totalanswersreport';
import TopSkippedReport from "./topskippedreport/topskippedreport";
import UniqueTriviaUsersReport from './uniquetriviausersresport/uniquetriviausers';
import {APIStringtoDate} from "../../../../utils/date";
import AverageInViewReport from './avginviewreport/avginviewreport';



/* ========== ~~~~~~~~~~ TRIVIA & POLLS - Analytics PAGE ~~~~~~~~~~ ========== */

export default injectSheet(styles)((props) => {
    // state
    const { accountUUID } = useSelector((state) => state.session);
    const { filters } = useSelector((state) => state.app);
    const { sites, dateRange, devices, compareTo, countries } = useSelector((state) => state.app.filters);

    const { lastUpdated } = useSelector(state => state.triviaAndPolls);

    const [selectedInterval, setSelectedInterval] = useState('daily');

    // actions
    const dispatch = useDispatch();

    // request reports on division load
    useEffect(() => {
        sendReportRequests()
    }, [sites, devices, compareTo, countries, selectedInterval]);


    const sendReportRequests = () => {
        getAvgInViewReport();
        getTotalAnswersReport();
        getUniqueTriviaUsers();
        getTriviaEngagementRate();
        getTopQuizAndPolls();
        getTopSkipped();
        getProductDataLastUpdated();
    }

    const getAvgInViewReport = () => dispatch(fetchAvgInViewReport(getRequestData()))
    const getProductDataLastUpdated = () => dispatch(fetchProductDataLastUpdated('TRIVIA'));
    const getUniqueTriviaUsers = () => dispatch(fetchUniqueTriviaUsersReport({requestData: getRequestData()}))
    const getTriviaEngagementRate = () => dispatch(fetchTriviaEngagementRateReport({requestData: getRequestData()}))
    const getTotalAnswersReport = () => dispatch(fetchTotalAnswersReport(getRequestData()));
    const getTopQuizAndPolls = () => dispatch(fetchTopCardsReport({requestData: getRequestData()}));
    const getTopSkipped= () => dispatch(fetchTopSkippedReport({requestData: getRequestData()}));

    const handleTopSkippedCardsExportAllClick = () => dispatch(fetchTopSkippedCardsExportAll(getRequestData()));
    const handleTopPollExportAllClick = () => dispatch(fetchTopPollsExportAll(getRequestData()));

    const getRequestData = () => ({
        accountUUID,
        sites: sites,
        startDate: dateRange.from,
        endDate: dateRange.to,
        devices: getDevices(devices, 'trivia'),
        granularity: selectedInterval.toUpperCase(),
        comparisonType: compareTo,
        countries: countries.length === 250 ? [] : countries
    });
    const getBannerText = useCallback(() => {
        // if start date is before June 3rd 2021
        const june3rdDate = new Date(2021, 5, 3)
        if (filters.dateRange.from && APIStringtoDate(filters.dateRange.from) < june3rdDate){
            return 'For dates prior to June 3, 2021, we are not able to accurately break down data by device or geo. ' +
                'To see all data correctly before that date, please remove any device or geo filters.';
        }
        return null;
    }, [filters.dateRange]);

    return (
        <Page name={'triviapollsanalyticspage'} klass={`${props.classes.triviapollsanalyticspage}`} >
            <Header>
                <GlobalFilter onFilterApply={sendReportRequests}/>
            </Header>
            <Content>
                <InContentMessage />
                <PageHeader lastUpdatedTimestamp={lastUpdated}
                            title={'Trivia & Polls'}
                            subtitle={'Analytics'}
                            dataTip={true}
                            bannerText={getBannerText()} />
                <PageContent>
                    <AverageInViewReport selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval} />
                    <TotalAnswersReport selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval} />
                    <UniqueTriviaUsersReport selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval} />
                    <TopPollandQuizReport onExportAllClick={handleTopPollExportAllClick}/>
                    <TopSkippedReport onExportAllClick={handleTopSkippedCardsExportAllClick}/>
                </PageContent>
            </Content>
        </Page>
    );
});
